import { useAppDispatch, useAppSelector } from 'app/store';
import { LoadAudioAction } from 'app/store/AudioPlayer/slice.types';
import { getAudioPlayer } from 'app/store/AudioPlayer/selectors';
import {
  closePlayerAction,
  loadAudioAction,
  setIsPlayingAction,
  setIsWaveformOpen,
  setRepeatAction,
  skipBackToPreviousAudioAction,
  skipToNextAudioAction,
} from 'app/store/AudioPlayer/slice';
import { useCallback } from 'react';
import { loadWaveformData } from './useAudioLoading';

export const useAudioPlayer = () => {
  const {
    isPlaying: audioPlayerPlaying,
    queueIndex = 0,
    queue = [],
  } = useAppSelector(getAudioPlayer);
  const dispatch = useAppDispatch();
  const currentAudio = queue[queueIndex];
  const { player } = window;

  const loadAudio = async (data: LoadAudioAction) => {
    player?.stop();
    dispatch(loadAudioAction(data));
  };

  const skip = useCallback(() => {
    if (queue.length - 1 >= queueIndex) {
      player?.stop();
    }
    dispatch(skipToNextAudioAction());
  }, [dispatch, player, queue?.length, queueIndex]);

  const skipBack = useCallback(() => {
    if (queueIndex > 0) {
      player?.stop();
    }
    dispatch(skipBackToPreviousAudioAction());
  }, [dispatch, player, queueIndex]);

  const playPauseAudio = useCallback(() => {
    dispatch(setIsPlayingAction(!audioPlayerPlaying));
    if (currentAudio?.audioSrc) {
      player?.playPause();
    }
  }, [audioPlayerPlaying, dispatch, player, currentAudio]);

  const stop = () => {
    player?.stop();
    player?.empty();
    navigator.mediaSession?.setActionHandler('play', null);
    navigator.mediaSession?.setActionHandler('pause', null);
    navigator.mediaSession?.setActionHandler('previoustrack', null);
    navigator.mediaSession?.setActionHandler('nexttrack', null);
    dispatch(closePlayerAction());
  };

  const retryWaveformLoad = async (waveformSrc?: string) => {
    const peaks = await loadWaveformData(dispatch, waveformSrc);

    if (peaks) {
      player?.setOptions({ peaks: [peaks] });
    }
  };

  const repeat = (value: boolean) => {
    dispatch(setRepeatAction(value));
  };

  return {
    player,
    stop,
    playAudio: player?.play,
    pauseAudio: player?.pause,
    playPauseAudio,
    retryWaveformLoad,
    setIsWaveformOpen: (open: boolean) => dispatch(setIsWaveformOpen(open)),
    loadAudio,
    skip,
    skipBack,
    repeat,
  };
};
